import { useRef, useCallback } from "react";
import { useVerificationMethod } from "components/ModalVerificationMethod/useVerificationMethod";
import {
  getStakeOtp,
  claimStakingRequest,
  confirmClaimStakingRequest,
} from "services/app";
import { error as notifyError } from "Notification";
import { useApp } from "contexts";

/**
 * Hook for handling claim staking request flow
 * @param {Object} options - Configuration options
 * @param {string|number} options.stakeRecordId - The stake record ID to claim
 * @param {Function} options.onSuccess - Callback function called when claim is successful
 * @returns {Function} claimStaking - Function to initiate the claim process
 */
export const useClaimStaking = ({ stakeRecordId, onSuccess }) => {
  const { T, setLoading, user } = useApp();
  const verification = useVerificationMethod();
  const txIdRef = useRef(undefined);

  const claimStaking = useCallback(async (options = {}) => {
    if (!stakeRecordId) {
      notifyError(T("Stake information not found"));
      return;
    }

    verification.verify({
      chooseMethod: options?.chooseMethod,
      actionText: T("Claim"),
      secondaryActionText: T("Back"),
      skipTwoFa: !user?.is_twofa,
      resendEachChooseMethod: true,
      sendOtp: async (method) => {
        try {
          setLoading(true);
          const res = await claimStakingRequest({
            stakeRecordId,
            otp_method: method,
          });
          const txIdResult = res?.data?.txId;
          if (res.status !== 200 && !txIdResult) {
            return {
              success: false,
              message:
                res?.data?.error?.message ||
                T("Failed to send OTP. Please try again later."),
            };
          }

          const result = await getStakeOtp({
            txId: txIdResult,
            otp_method: method,
          });
          if (result?.data?.success) {
            txIdRef.current = Number(txIdResult);
            return {
              success: true,
            };
          } else {
            return {
              success: false,
              message:
                result?.data?.error?.message ||
                T("Failed to send OTP. Please try again later."),
            };
          }
        } catch (err) {
          console.error("Error sending OTP for claim staking:", err);
          const message =
            err?.response?.data?.error?.message ||
            T("Failed to send OTP. Please try again later.");
          return {
            success: false,
            message,
          };
        } finally {
          setLoading(false);
        }
      },
      verifyOtp: async (method, otp) => {
        try {
          setLoading(true);
          const res = await confirmClaimStakingRequest({
            txId: txIdRef.current,
            emailCode: otp,
          });

          if (res.status === 200 && res?.data?.success) {
            return {
              success: true,
            };
          }

          const message = res?.data?.error?.message;

          if (message === "otp_invalid") {
            return {
              success: false,
              otpError: T("Verification Code is not correct."),
            };
          }

          if (typeof message === "string" && message?.includes("missing revert data (action=\"estimateGas\"")) {
            notifyError(
              T("Insufficient {token} balance to cover gas fee.").replace("{token}", "POL")
            );
            return {
              success: false,
              otpError: T("Insufficient {token} balance to cover gas fee.").replace("{token}", "POL"),
            };
          }

          notifyError(
            T(
              message ||
                "Failed to create your request. Please try again later."
            )
          );
          return {
            success: false,
            otpError: T(
              "Failed to create your request. Please try again later."
            ),
          };
        } catch (err) {
          console.error("Error verifying OTP for claim staking:", err);
          if (err?.response?.data?.error?.message === "otp_invalid") {
            return {
              success: false,
              otpError: T("Verification Code is not correct."),
            };
          }
          notifyError(
            T(
              err?.response?.data?.error?.message ||
                "Failed to create your request. Please try again later."
            )
          );
          return {
            success: false,
            otpError: T(
              "Failed to create your request. Please try again later."
            ),
          };
        } finally {
          setLoading(false);
        }
      },
      action: async (method, otp, twoFa) => {
        try {
          setLoading(true);
          const res = await confirmClaimStakingRequest({
            txId: txIdRef.current,
            emailCode: !user?.is_twofa ? otp : undefined,
            twofaCode: user?.is_twofa ? twoFa : undefined,
          });

          if (res.status === 200 && res?.data?.success) {
            if (onSuccess) {
              onSuccess();
            }
            return {
              success: true,
            };
          }

          const message = res?.data?.error?.message;

          if (message === "otp_invalid") {
            return {
              success: false,
              otpError: T("Verification Code is not correct."),
            };
          }

          if (message === "2fa_invalid") {
            return {
              success: false,
              twoFaError: T("2FA Code invalid"),
            };
          }

          if (typeof message === "string" && message?.includes("missing revert data (action=\"estimateGas\"")) {
            notifyError(
              T("Insufficient {token} balance to cover gas fee.").replace("{token}", "POL")
            );
            return {
              success: false,
              otpError: T("Insufficient {token} balance to cover gas fee.").replace("{token}", "POL"),
            };
          }

          notifyError(
            T(
              message ||
                "Failed to create your request. Please try again later."
            )
          );

          return {
            success: false,
            twoFaError: user?.is_twofa
              ? T("Failed to create your request. Please try again later.")
              : undefined,
            otpError: !user?.is_twofa
              ? T("Failed to create your request. Please try again later.")
              : undefined,
          };
        } catch (err) {
          console.error("Error confirming claim staking:", err);
          if (err?.response?.data?.error?.message === "otp_invalid") {
            return {
              success: false,
              otpError: T("Verification Code is not correct."),
            };
          }
          if (err?.response?.data?.error?.message === "2fa_invalid") {
            return {
              success: false,
              twoFaError: T("2FA Code invalid"),
            };
          }
          notifyError(
            T(
              err?.response?.data?.error?.message ||
                "Failed to create your request. Please try again later."
            )
          );
          return {
            success: false,
            twoFaError: user?.is_twofa
              ? T("Failed to create your request. Please try again later.")
              : undefined,
            otpError: !user?.is_twofa
              ? T("Failed to create your request. Please try again later.")
              : undefined,
          };
        } finally {
          setLoading(false);
        }
      },
    });
  }, [stakeRecordId, onSuccess, T, setLoading, user, verification]);

  return claimStaking;
};

